import { ChangeEvent, ReactNode } from "react"

import {
  Pagination as MuiPagination,
  PaginationItem,
  Stack,
  Typography,
} from "@mui/material"
import { useTranslation } from "react-i18next"

const PreviousLabel = () => {
  const { t } = useTranslation()
  return <span>‹ {t("previous")}</span>
}

const NextLabel = () => {
  const { t } = useTranslation()
  return <span>{t("next")} ›</span>
}

const itemSx = {
  height: 32,
  minWidth: 32,
  m: 0,
  px: 1.5,
  borderRadius: "8px",
  bgcolor: "mockup.primary95",
  color: "mockup.primary60",
  fontSize: "14px",
  fontWeight: 600,
  "&:hover": { bgcolor: "mockup.primary90" },
  "&.Mui-focusVisible": {
    outline: "2px solid",
    outlineColor: "mockup.primary60",
    outlineOffset: "2px",
  },
  "&.Mui-selected, &.Mui-selected:hover, &.Mui-selected.Mui-focusVisible": {
    bgcolor: "mockup.primary60",
    color: "common.white",
  },
  "&.MuiPaginationItem-ellipsis": {
    bgcolor: "transparent",
    color: "mockup.neutral30",
    px: 0.5,
  },
}

const Pagination = ({
  page,
  count,
  onChange,
  summary,
}: {
  page: number
  count: number
  onChange: (page: number) => void
  summary?: ReactNode
}) => {
  const { t } = useTranslation()

  const getItemAriaLabel = (
    type: string,
    itemPage: number | null,
    selected: boolean,
  ) => {
    if (type === "page")
      return selected
        ? t("currentPage", { page: itemPage })
        : t("goToPage", { page: itemPage })
    if (type === "previous") return t("goToPreviousPage")
    if (type === "next") return t("goToNextPage")
    if (type === "first") return t("goToFirstPage")
    return t("goToLastPage")
  }

  return (
    <Stack
      direction={{ mobile: "column", tablet: "row" }}
      alignItems={{ mobile: "flex-start", tablet: "center" }}
      justifyContent="space-between"
      gap={2}
    >
      {summary ? (
        <Typography variant="body2" color="mockup.neutral30">
          {summary}
        </Typography>
      ) : (
        <span />
      )}
      {count > 1 && (
        <MuiPagination
          page={page}
          count={count}
          onChange={(_: ChangeEvent<unknown>, next: number) => onChange(next)}
          aria-label={t("pagination")}
          getItemAriaLabel={getItemAriaLabel}
          sx={{ "& .MuiPagination-ul": { gap: 1 } }}
          renderItem={(item) => (
            <PaginationItem
              {...item}
              slots={{ previous: PreviousLabel, next: NextLabel }}
              sx={itemSx}
            />
          )}
        />
      )}
    </Stack>
  )
}

export default Pagination
